import React, { Component } from 'react';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import CssBaseline from '@material-ui/core/CssBaseline';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import withStyles from '@material-ui/styles/withStyles';
import {ACCESS_TOKEN} from "../Constants";
import {login, verify2fa, resend2fa} from "../util/ApiUtils";
import 'react-notifications/lib/notifications.css';
// Notifications are present in the app but we'll prefer inline errors for this form
import PropTypes from "prop-types";
import { t, setLanguage, getLanguage } from "../i18n";

const classes = (theme => ({
    '@global': {
        body: {
            backgroundColor: theme.palette.common.white,
        },
    },
    paper: {
        marginTop: theme.spacing(8),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    languageSelect: {
        position: 'absolute',
        right: theme.spacing(2),
        top: theme.spacing(2),
        width: 96,
        '& .MuiInputBase-input': {
            fontSize: '0.78rem'
        }
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
}));

class SignIn extends Component {
    constructor(props) {
        super(props);
        this.state = {
            email: '',
            password: '',
            twoFactorRequired: false,
            twoFactorCode: '',
            pendingAccessToken: null,
            formError: '',
            twoFactorError: '',
            lang: getLanguage(),
            resendDisabled: false,
            resendCountdown: 0,
            submitting: false
        };
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleLanguageChange = this.handleLanguageChange.bind(this);
        this.handleResend = this.handleResend.bind(this);
        this.handleReset = this.handleReset.bind(this);
        this.resendTimer = null;
    }

    componentDidMount() {
        try {
            const params = new URLSearchParams(window.location.search);
            const qlang = params.get('lang');
            if (qlang === 'zh') {
                // Respect query param by updating the global language and local state so the page reflects it
                try { setLanguage('zh'); } catch(e) { /* ignore */ }
                this.setState({ lang: 'zh' });
            }
        } catch (e) {
            // ignore parsing errors
        }
    }

    componentWillUnmount() {
        if (this.resendTimer) clearInterval(this.resendTimer);
    }

    handleLanguageChange(event) {
        const value = event && event.target ? event.target.value : 'en';
        // Update global UI language immediately so the sign-in page reflects the selection,
        // but account language will be applied on successful login (server value).
        try { setLanguage(value); } catch (e) { /* ignore */ }
        this.setState({ lang: value });
    }

    handleSubmit(event) {
        event.preventDefault();
        // prevent duplicate submits
        if (this.state.submitting) return;
        this.setState({ submitting: true, formError: '' });
        // If we're in the second step (2FA), verify the code
        if (this.state.twoFactorRequired) {
            const code = (this.state.twoFactorCode || '').trim();
            this.setState({ twoFactorError: '' });
            verify2fa(code, this.state.pendingAccessToken)
                .then(() => {
                    if (this.state.pendingAccessToken) {
                        localStorage.setItem(ACCESS_TOKEN, this.state.pendingAccessToken);
                    }
                    this.setState({ twoFactorRequired: false, pendingAccessToken: null, twoFactorCode: '', twoFactorError: '' });
                    this.props.onLogin();
                })
                .catch(err => {
                    let msg = t('auth.signIn.errors.invalid2fa');
                    if (typeof err === 'string') {
                        msg = err;
                    } else if (err && typeof err.message === 'string') {
                        msg = err.message;
                    } else if (err && err.error && typeof err.error.message === 'string') {
                        msg = err.error.message;
                    }
                    this.setState({ twoFactorError: msg || t('auth.signIn.errors.invalid2fa'), submitting: false });
                });
            return;
        }

        // First step: username/password
        this.setState({ formError: '' });
        const loginRequest = Object.assign({},
            {usernameOrEmail : this.state.email},
            {password: this.state.password}
        );
        login(loginRequest)
            .then(response => {
                // If server says 2FA is required for this account, switch to 2FA step
                if (response && response.requiresTwoFactor) {
                    this.setState({
                        twoFactorRequired: true,
                        pendingAccessToken: response.accessToken,
                        formError: '',
                        submitting: false
                    });
                    // A 2FA code was just generated on the server as part of sign-in; disable resend for 60s
                    try { this.startResendCountdown(60); } catch (e) { /* ignore */ }
                } else if (response && response.accessToken) {
                    // No 2FA required: complete login immediately
                    localStorage.setItem(ACCESS_TOKEN, response.accessToken);
                    this.setState({ formError: '' });
                    this.props.onLogin();
                } else {
                    this.setState({ formError: t('auth.signIn.errors.unexpected'), submitting: false });
                }
             }).catch(error => {
                // error may be a string, an object with message, or an object with error.message
                if(error && error.status === 401) {
                    this.setState({ formError: t('auth.signIn.errors.invalidCredentials'), submitting: false });
                } else {
                    let msg = t('auth.signIn.errors.generic');
                    if (typeof error === 'string') {
                        msg = error;
                    } else if (error && typeof error.message === 'string') {
                        msg = error.message;
                    } else if (error && error.error && typeof error.error.message === 'string') {
                        msg = error.error.message;
                    }
                    this.setState({ formError: msg, submitting: false });
                }
             });
    }

    handleChange({ target }) {
        this.setState({
            [target.name]: target.value
        });
    }

    handleResend() {
        // Request server to resend using pendingAccessToken (must be present)
        const token = this.state.pendingAccessToken;
        if (!token) return;
        // start a local 60s countdown immediately (server will also enforce rate limit)
        this.startResendCountdown(60);
        resend2fa(token).then(() => {
            // success — server resent code. Nothing else to do, countdown already running.
        }).catch(err => {
            // If server replies with rate limitation, try to parse remaining seconds and adjust countdown
            let msg = t('auth.signIn.errors.unableResend');
             try {
                 if (err && err.status === 429) {
                    // server may include message like 'Resend allowed after X seconds'
                    const bodyMsg = err && err.message ? err.message : (err && err.error && err.error.message ? err.error.message : null);
                    if (bodyMsg) {
                        const m = bodyMsg.match(/(\d+)\s*seconds?/i);
                        if (m && m[1]) {
                            const sec = parseInt(m[1], 10);
                            if (!Number.isNaN(sec)) this.startResendCountdown(sec);
                        }
                        msg = bodyMsg;
                    }
                } else if (err && typeof err.message === 'string') {
                    msg = err.message;
                } else if (err && err.error && typeof err.error.message === 'string') {
                    msg = err.error.message;
                }
            } catch (e) {
                // ignore parse errors
            }
            this.setState({ formError: msg });
        });
    }

    handleReset() {
        // Clear any countdown and reset 2FA state so the user can try a different login
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
        }
        this.setState({
            twoFactorRequired: false,
            pendingAccessToken: null,
            twoFactorCode: '',
            formError: '',
            twoFactorError: '',
            resendDisabled: false,
            resendCountdown: 0,
            // clear credentials so the user can enter a different email/password
            email: '',
            password: ''
        });
    }

    startResendCountdown(seconds) {
        // clear any existing timer
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
        }
        const secs = typeof seconds === 'number' && seconds > 0 ? Math.floor(seconds) : 60;
        this.setState({ resendDisabled: true, resendCountdown: secs });
        this.resendTimer = setInterval(() => {
            this.setState(prev => {
                if (prev.resendCountdown <= 1) {
                    clearInterval(this.resendTimer);
                    this.resendTimer = null;
                    return { resendDisabled: false, resendCountdown: 0 };
                }
                return { resendCountdown: prev.resendCountdown - 1 };
            });
        }, 1000);
    }

    render() {
        const { classes } = this.props;
        return (
            <div>
                <Container component="main" maxWidth="xs" style={{ position: 'relative' }}>
                    {/* small top-right language selector */}
                    <div className={classes.languageSelect}>
                        <TextField
                            select
                            variant="outlined"
                            size="small"
                            id="language"
                            name="language"
                            value={this.state.lang}
                            onChange={ this.handleLanguageChange }
                            InputProps={{
                                style: { paddingTop: 4, paddingBottom: 4 }
                            }}
                        >
                            <MenuItem value="en">{ t('auth.signIn.lang.en') }</MenuItem>
                            <MenuItem value="zh">{ t('auth.signIn.lang.zh') }</MenuItem>
                        </TextField>
                    </div>
                     <CssBaseline />
                     <div className={classes.paper}>
                        <Avatar className={classes.avatar}>
                             <LockOutlinedIcon />
                         </Avatar>
                        <Typography component="h1" variant="h5">
                            {t('auth.signIn.title')}
                        </Typography>
                        <form onSubmit={this.handleSubmit} className={classes.form}>

                            <TextField
                                 variant="outlined"
                                 margin="normal"
                                 required
                                 fullWidth
                                 id="email"
                                 label={t('auth.signIn.emailLabel')}
                                 name="email"
                                 autoComplete="email"
                                 autoFocus
                                 value={this.state.email}
                                 onChange={ this.handleChange }
                             />
                             <TextField
                                 variant="outlined"
                                 margin="normal"
                                 required
                                 fullWidth
                                 name="password"
                                 label={t('auth.signIn.passwordLabel')}
                                 type="password"
                                 id="password"
                                 autoComplete="current-password"
                                 value={this.state.password}
                                 onChange={ this.handleChange }
                             />

                            {/* 2FA field: only show after correct username/password */}
                            {this.state.twoFactorRequired && (
                                <TextField
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    fullWidth
                                    id="twoFactorCode"
                                    label={t('auth.signIn.twoFactorLabel')}
                                    name="twoFactorCode"
                                    autoComplete="one-time-code"
                                    onChange={ this.handleChange }
                                    error={!!this.state.twoFactorError}
                                    helperText={this.state.twoFactorError}
                                />
                            )}

                            {this.state.twoFactorRequired && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginTop: 8 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={this.handleReset}
                                            disabled={!!this.state.resendDisabled}
                                        >
                                            { t('auth.signIn.reset') }
                                        </Button>
                                        <div style={{ width: 8 }} />
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={this.handleResend}
                                            disabled={!!this.state.resendDisabled}
                                        >
                                            { t('auth.signIn.resendCode') }
                                        </Button>
                                        <div style={{ width: 12 }} />
                                        {this.state.resendDisabled && (
                                            <Typography variant="body2" color="textSecondary">
                                                { t('auth.signIn.resendCountdown', { seconds: this.state.resendCountdown }) }
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            )}

                            {this.state.formError && !this.state.twoFactorRequired && (
                                <Typography color="error" variant="body2">{this.state.formError}</Typography>
                            )}

                            {/*<FormControlLabel
                                control={<Checkbox value="remember" color="primary" />}
                                label={ t('auth.signIn.rememberMe') }
                            />*/}
                            <Button
                                 type="submit"
                                 fullWidth
                                 variant="contained"
                                 color="primary"
                                 className={classes.submit}
                                 disabled={!!this.state.submitting}
                             >
                                 {this.state.twoFactorRequired ? t('auth.signIn.verify2faButton') : t('auth.signIn.signInButton')}
                             </Button>
                            {/* <Grid container>
                                <Grid item xs>
                                    <Link href="/forgot-password" variant="body2">
                                        { t('auth.signIn.forgotPassword') }
                                    </Link>
                                </Grid>
                                <Grid item>
                                    <Link href="/sign-up" variant="body2">
                                        { t('auth.signIn.signUpPrompt') }
                                    </Link>
                                </Grid>
                            </Grid> */}
                        </form>
                    </div>
                    {/* no notification container for inline feedback */}
                </Container>
            </div>
        );
    }
}

SignIn.propTypes = {
    onLogin: PropTypes.func.isRequired,
};

export default withStyles(classes)(SignIn);
